import { getPartnerBySlug, isPartnerEnabled } from '@/config/partners';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  WhiteLabelContext,
  clearPartnerSlug,
  getStoredPartnerSlug,
  type WhiteLabelContextValue,
} from './white-label';

interface WhiteLabelProviderProps {
  children: ReactNode;
}

/**
 * Provider for white-label branding context.
 *
 * Reads partner slug from localStorage and applies partner branding.
 * Partner is set when user visits /p/[partner] route.
 */
export function WhiteLabelProvider({ children }: WhiteLabelProviderProps) {
  const [partnerSlug, setPartnerSlugState] = useState<string | null>(null);
  const [isHydrated, setIsHydrated] = useState(false);

  // Read from localStorage on mount
  useEffect(() => {
    const stored = getStoredPartnerSlug();
    setPartnerSlugState(stored);
    setIsHydrated(true);
  }, []);

  // Clear stale localStorage if partner flag has been turned off
  useEffect(() => {
    if (!isHydrated || !partnerSlug) return;
    if (!isPartnerEnabled(partnerSlug)) {
      clearPartnerSlug();
      setPartnerSlugState(null);
    }
  }, [isHydrated, partnerSlug]);

  const value = useMemo<WhiteLabelContextValue>(() => {
    if (!isHydrated) {
      return {
        isWhiteLabel: false,
        partnerName: null,
        partnerLogoUrl: null,
        appName: 'Heidi',
      };
    }

    const partner = partnerSlug ? getPartnerBySlug(partnerSlug) : null;
    const isEnabled = partnerSlug ? isPartnerEnabled(partnerSlug) : false;

    if (!partner || !isEnabled) {
      return {
        isWhiteLabel: false,
        partnerName: null,
        partnerLogoUrl: null,
        appName: 'Heidi',
      };
    }

    return {
      isWhiteLabel: true,
      partnerName: partner.name,
      partnerLogoUrl: partner.logoUrl,
      appName: partner.name,
    };
  }, [partnerSlug, isHydrated]);

  return (
    <WhiteLabelContext.Provider value={value}>{children}</WhiteLabelContext.Provider>
  );
}
